import { medicalReceiptFormatDateTimeByDash2 } from './format';

/**
 * 숫자에 천 단위 구분자(,)를 추가하는 함수
 * @param {string} value - 변환할 숫자 문자열
 * @returns {string} 천 단위 구분자가 추가된 문자열
 */
const formatNumber = (value) => {
  if (!value || value === '0') return '';
  return Number(value).toLocaleString('ko-KR');
};

/**
 * Binds receipt items data to the appropriate table cells in the receipt HTML
 * @param {Object} receiptData - The mock data or API response containing receipt items
 * @param {HTMLElement} containerElement - The container element (reference to the rendered HTML)
 * @param {string} _hspId - Hospital ID (reserved for future use)
 */
// eslint-disable-next-line no-unused-vars
export const bindReceiptItemsData = (receiptData, containerElement, _hspId) => {
  if (!receiptData || !receiptData.receipt_items || !containerElement) {
    return;
  }

  const tbody = containerElement.querySelector('tbody[name="receipt_items"]');
  if (!tbody) {
    console.error('Receipt items tbody not found');
    return;
  }

  // Get all rows in the tbody
  const rows = Array.from(tbody.querySelectorAll('tr'));

  // 데이터 행만 필터링: 4개의 가격 span이 모두 있는 행
  const requiredNames = [
    'partial_patient_responsibility_price',
    'insurance_price',
    'full_patient_responsibility_price',
    'uninsurance_price',
  ];

  const dataRows = rows.filter((row) => {
    const spans = Array.from(row.querySelectorAll('span[name]'));
    const names = spans.map((span) => span.getAttribute('name'));
    return requiredNames.every((name) => names.includes(name));
  });

  receiptData.receipt_items.forEach((item, index) => {
    if (index >= dataRows.length) {
      console.warn(`Receipt item index ${index} exceeds available rows (${dataRows.length})`);
      return;
    }
    const row = dataRows[index];

    requiredNames.forEach((name) => {
      const span = row.querySelector(`span[name="${name}"]`);
      if (span) {
        const value = item[name] || 0;
        span.textContent = formatNumber(value);
      }
    });
  });
};

/**
 * Binds additional items data to additional_items spans in the receipt HTML
 * @param {Object} receiptData - The mock data or API response containing additional_items
 * @param {HTMLElement} containerElement - The container element (reference to the rendered HTML)
 */
export const bindAdditionalItemsData = (receiptData, containerElement) => {
  if (!receiptData || !receiptData.additional_items || !containerElement) {
    return;
  }

  // additional_items item_nm과 item_value span을 모두 찾기
  const itemNmSpans = Array.from(containerElement.querySelectorAll('span[name="additional_items item_nm"]'));
  const itemValueSpans = Array.from(containerElement.querySelectorAll('span[name="additional_items item_value"]'));

  // additional_items를 순서대로 span에 매핑
  receiptData.additional_items.forEach((item, index) => {
    if (index >= itemNmSpans.length || index >= itemValueSpans.length) {
      console.warn(
        `Additional item index ${index} exceeds available spans (item_nm: ${itemNmSpans.length}, item_value: ${itemValueSpans.length})`,
      );
      return;
    }

    const itemNmSpan = itemNmSpans[index];
    const itemValueSpan = itemValueSpans[index];

    // item_value가 없거나 '0' 또는 0이면 해당 span을 비움
    if (!item.item_value || item.item_value === '0' || item.item_value === 0 || String(item.item_value).trim() === '') {
      if (itemNmSpan) itemNmSpan.textContent = '';
      if (itemValueSpan) itemValueSpan.textContent = '';
      return;
    }

    // item_nm과 item_value 설정
    if (itemNmSpan) {
      itemNmSpan.textContent = item.item_nm || '';
    }
    if (itemValueSpan) {
      itemValueSpan.textContent = formatNumber(item.item_value);
    }
  });
};
/**
 * Binds med_rsv data to med_rsv spans in the receipt HTML
 * @param {Object} receiptData - The mock data or API response containing med_rsv
 * @param {HTMLElement} containerElement - The container element (reference to the rendered HTML)
 */
export const bindMedRsvData = (receiptData, containerElement) => {
  if (!receiptData || !receiptData.med_rsv || !containerElement) {
    return;
  }

  const medRsvDiv = containerElement.querySelector('div[name="med_rsv"]');

  // item_nm과 item_value 설정
  if (medRsvDiv) {
    medRsvDiv.innerHTML = receiptData.med_rsv
      .map(
        (item) => `
      <div style="display: flex; flex-direction: column; font-size: 8px;">
        <div style="display: flex; flex-direction: row;">
          <span>${item.dept_nm || ''}</span>
          <span>${item.tel_no || ''}</span>
          <span>${item.medr_nm || ''}</span>
        </div>
        <div style="display: flex; flex-direction: row; justify-content: space-around;">
          <span>${medicalReceiptFormatDateTimeByDash2(item.med_dt) || ''}</span>
          <span>${item.rcpyn || ''}</span>
        </div>
      </div>
    `,
      )
      .join('');
  }
};
/**
 * Binds place_to_go data to place_to_go spans in the receipt HTML
 * @param {Object} receiptData - The mock data or API response containing place_to_go
 * @param {HTMLElement} containerElement - The container element (reference to the rendered HTML)
 */
export const bindPlaceToGoData = (receiptData, containerElement) => {
  if (!receiptData || !receiptData.place_to_go || !containerElement) {
    return;
  }

  const placetGoDiv = containerElement.querySelector('div[name="place_to_go"]');
  if (placetGoDiv) {
    placetGoDiv.innerHTML = receiptData.place_to_go
      .map(
        (item) => `
        <div style="display: flex; flex-direction: row; gap: 5px;">
          <span style="min-width: 65px;">${item.floor || ''}</span>
          <span>${item.place || ''}</span>
        </div>
      `,
      )
      .join('');
  }
};
